import React from 'react';
import { Clock, Globe, Award, Target, BarChart3, Building2, TrendingUp, Cpu, FileText } from 'lucide-react';
import { formatDateRange } from '@/lib/formatters';
import { GroupedTypeSpecificParams } from '../types/research-parameters-types';

export const getResearchTypeIcon = (type: string) => {
  switch (type) {
    case 'Market Analysis':
      return React.createElement(BarChart3, { size: 16, className: "text-purple-500" });
    case 'Business Analysis':
      return React.createElement(Building2, { size: 16, className: "text-green-500" });
    case 'Marketing Analysis':
      return React.createElement(TrendingUp, { size: 16, className: "text-purple-500" });
    case 'Technical Research':
      return React.createElement(Cpu, { size: 16, className: "text-orange-500" });
    case 'Competitive Research':
      return React.createElement(Target, { size: 16, className: "text-red-500" });
    case 'Industry Report':
      return React.createElement(FileText, { size: 16, className: "text-indigo-500" });
    default:
      return React.createElement(BarChart3, { size: 16, className: "text-gray-500" });
  }
};

export const getParameterIcon = (key: string, parameters: Record<string, any>) => {
  switch (key.toLowerCase()) {
    case 'timeframe':
    case 'timeframe_label':
      return React.createElement(Clock, { size: 14, className: "text-purple-500" });
    case 'country':
      return React.createElement(Globe, { size: 14, className: "text-blue-500" });
    case 'source_type':
    case 'source_quality':
      return React.createElement(Award, { size: 14, className: "text-yellow-500" });
    case 'research_type':
      return getResearchTypeIcon(parameters[key] || '');
    default:
      return React.createElement(Target, { size: 14, className: "text-gray-500" });
  }
};

export const formatValue = (value: any): string => {
  if (typeof value === 'string') {
    if (value.toLowerCase() === 'global') return 'Global';
    if (value.toLowerCase() === 'current') return 'Current';
    return value.replace(/_/g, ' ');
  }
  if (typeof value === 'object' && value !== null) {
    return JSON.stringify(value, null, 2);
  }
  return String(value);
};

export const formatKey = (key: string): string => {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase());
};

export const formatResearchType = (typeKey: string): string => {
  return typeKey
    .replace(/_/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase());
};

export const formatParameterName = (paramKey: string): string => {
  return paramKey
    .replace(/_/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase());
};

export const formatTimeframeDisplay = (parameters: Record<string, any>): string => {
  const dateRangeStart = parameters.date_range_start;
  const dateRangeEnd = parameters.date_range_end;
  
  // ONLY show the clean date range when available
  if (dateRangeStart && dateRangeEnd) {
    const dateRange = formatDateRange(dateRangeStart, dateRangeEnd);
    if (dateRange) {
      return dateRange;
    }
  }
  
  // Fallback to timeframe label if no date range
  if (parameters.timeframe_label) {
    return parameters.timeframe_label;
  }
  
  // Final fallback to timeframe value
  if (parameters.timeframe) {
    return formatValue(parameters.timeframe);
  }
  
  return 'Not specified';
};

/**
 * Parse type-specific parameters from the research parameters
 * Handles both namespaced keys (e.g., 'technology_and_innovation_tech_domain') 
 * and non-namespaced keys (e.g., 'tech_domain')
 */
export const parseTypeSpecificParameters = (typeSpecificParams: Record<string, any>): GroupedTypeSpecificParams => {
  const groupedParams: GroupedTypeSpecificParams = {};
  // Map of known filter keys to their research type
  const knownFilterKeyMap: Record<string, string> = {
    // Technology & Innovation
    'tech_domain': 'technology_and_innovation',
    'research_focus': 'technology_and_innovation',
    
    // Personal Development
    'development_area': 'personal_development',
    'outcome_focus': 'personal_development',
    
    // Business & Professional
    'industry_focus': 'business_and_professional',
    'analysis_type': 'business_and_professional',
    
    // Content & Creative
    'content_type': 'content_and_creative',
    'audience_focus': 'content_and_creative',
    
    // Academic & Learning
    'subject_area': 'academic_and_learning',
    'research_method': 'academic_and_learning',
    
    // Current Events & News
    'topic_area': 'current_events_and_news',
    'coverage_type': 'current_events_and_news'
  };

  Object.entries(typeSpecificParams).forEach(([key, value]) => {
    if (value && typeof value === 'string' && value.trim() !== '') {
      let researchType: string;
      let parameterName: string;
      
      const parts = key.split('_');
      
      // Handle namespaced keys (legacy format)
      if (parts.length >= 3) {
        // For keys like 'technology_and_innovation_tech_domain'
        researchType = parts.slice(0, 2).join('_');
        parameterName = parts.slice(2).join('_');
      } 
      // Handle non-namespaced keys (new format)
      else if (knownFilterKeyMap[key]) {
        // For keys like 'tech_domain'
        researchType = knownFilterKeyMap[key];
        parameterName = key;
      }
      // Skip unknown keys
      else {
        return;
      }
      
      if (!groupedParams[researchType]) {
        groupedParams[researchType] = [];
      }
      
      groupedParams[researchType].push({
        name: formatParameterName(parameterName),
        value: formatValue(value)
      });
    }
  });

  return groupedParams;
};

export const truncateQuestion = (question: string, maxLength: number = 75): string => {
  if (question.length <= maxLength) return question;
  return question.substring(0, maxLength) + '...';
};

export const filterParameters = (parameters: Record<string, any>) => {
  // Only include parameters that have actual values (not null or empty)
  const filteredParams = Object.entries(parameters).filter(([key, value]) => 
    key !== 'original_prompt' && 
    key !== 'research_type' && 
    key !== 'timeframe_label' &&
    key !== 'date_range_start' &&
    key !== 'date_range_end' &&
    value !== null &&
    value !== undefined &&
    value !== ''
  );

  // Country parameters - only show if there's a value
  const locationParams = filteredParams.filter(([key, value]) => 
    key === 'country' && value
  );
  
  // Source type parameters - check both source_type and source_quality for backward compatibility
  const sourceParams = filteredParams.filter(([key, value]) => 
    (key === 'source_type' || key === 'source_quality') && value
  );
  
  // Other main parameters
  const mainParams = filteredParams.filter(([key, value]) => 
    !key.includes('type_specific') && 
    key !== 'type_specific_filters' && 
    key !== 'country' && 
    key !== 'timeframe' &&
    key !== 'source_type' &&
    key !== 'source_quality' &&
    value
  );

  return { 
    locationParams, 
    sourceParams, 
    mainParams, 
    filteredParams 
  };
};
